import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Info, Scissors } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime, fmtNumber, fmtPercent } from '@/shared/lib/format'
import type { AnalyticsEnvelope } from '@/shared/api/types'
import { ScopingBadge } from './vocab'
import { listPrograms } from './api'

/**
 * The honesty widgets.
 *
 * Phase 5's whole argument is that a number without its denominator, its
 * exclusions or its provenance is worse than no number. Rather than restating
 * that on twelve screens and drifting on eleven of them, every screen composes
 * these six components — so "measured for 312 of 4,180" looks identical
 * everywhere and a screen that forgets one is visibly different from its
 * neighbours.
 */

// ── a value that could not be computed ──────────────────────────────────────

/**
 * A null with a reason. NEVER a zero.
 *
 * PollSphere rendered `0%` for "we did not measure this", which is a claim that
 * a programme changed nothing — a claim nobody made. An em dash plus the
 * server's own sentence is the honest rendering, and the sentence is the part
 * that tells the reader what to do about it.
 */
export function Unavailable({ reason, short }: { reason?: string | null; short?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground" title={reason ?? undefined}>
      <span aria-hidden>—</span>
      {!short && reason && <span className="text-erp-xs">{reason}</span>}
      {short && reason && <Info className="h-3 w-3 opacity-60" aria-label={reason} />}
    </span>
  )
}

/**
 * A number, or the reason there isn't one. The single most-used component in
 * the feature.
 */
export function Value({
  value, reason, format = fmtNumber, suffix, className, shortReason,
}: {
  value?: string | number | null
  reason?: string | null
  format?: (n: number) => string
  suffix?: string
  className?: string
  shortReason?: boolean
}) {
  if (value === null || value === undefined || value === '') {
    return <Unavailable reason={reason} short={shortReason} />
  }
  const n = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(n)) return <Unavailable reason={reason} short={shortReason} />
  return <span className={className}>{format(n)}{suffix}</span>
}

/**
 * Uplift, in PERCENTAGE POINTS.
 *
 * The distinction from `Pct` is not pedantry and it is not cosmetic. `coverage`,
 * `dropRate` and `roundNumberShare` are 0–1 fractions and multiplying them by a
 * hundred is right. `meanUplift` is already in points — a programme whose
 * support moved three and a half points arrives as `3.5`. Rendering that
 * through `Pct` prints **350%**, next to a chart on the same screen printing
 * "+3.5 pp" for the identical field.
 */
export function Pp({ value, reason, shortReason }: {
  value?: string | number | null; reason?: string | null; shortReason?: boolean
}) {
  if (value === null || value === undefined || value === '') {
    return <Unavailable reason={reason} short={shortReason} />
  }
  const n = Number(value)
  if (Number.isNaN(n)) return <Unavailable reason={reason} short={shortReason} />
  return <span className="tabular-nums">{n > 0 ? '+' : ''}{n.toFixed(1)} pp</span>
}

/** A percentage held in the API as a 0–1 fraction. */
export function Pct({ value, reason, digits = 1, shortReason }: {
  value?: string | number | null; reason?: string | null; digits?: number; shortReason?: boolean
}) {
  return <Value value={value} reason={reason} shortReason={shortReason}
    format={(n) => fmtPercent(n, digits)} />
}

// ── measurement coverage ────────────────────────────────────────────────────

/**
 * "measured for 312 of 4,180 (7%)".
 *
 * Attached to EVERY uplift on every screen. A mean over 7% of a cohort is a
 * real number about a small group, and saying so is the difference between
 * analysis and decoration.
 */
export function MeasuredWith({ measured, measurable, className }: {
  measured: number; measurable: number; className?: string
}) {
  const pct = measurable > 0 ? measured / measurable : null
  return (
    <span
      className={cn('text-erp-xs text-muted-foreground whitespace-nowrap', className)}
      title={measurable === 0
        ? 'Nothing in this group could be measured.'
        : `A re-assessment was recorded for ${measured} of the ${measurable} people who could have been measured.`}
    >
      measured {fmtNumber(measured)}/{fmtNumber(measurable)}
      {pct !== null && ` (${fmtPercent(pct, 0)})`}
    </span>
  )
}

/**
 * The people a figure could not include, and why. Renders nothing when there
 * are none — an "excluded 0" line is noise, and hiding a non-zero one is the
 * defect.
 */
export function Excluded({ items, className }: {
  items: { count: number; label: string; hint?: string }[]
  className?: string
}) {
  const shown = items.filter((i) => i.count > 0)
  if (!shown.length) return null
  return (
    <p className={cn('text-erp-xs text-muted-foreground', className)}>
      Excluded:{' '}
      {shown.map((i, k) => (
        <span key={i.label}>
          {k > 0 && ' · '}
          <span title={i.hint}>{fmtNumber(i.count)} {i.label}</span>
        </span>
      ))}
    </p>
  )
}

/**
 * How many rows have no comparable identity. Rides beside every duplicate,
 * overlap and distinct-reach figure.
 *
 * "0 duplicates" over a table where half the rows cannot be compared is a lie
 * told with a true number.
 */
export function Unmatchable({ count }: { count: number }) {
  if (!count) return null
  return (
    <p className="text-erp-xs text-amber-700 dark:text-amber-300">
      {fmtNumber(count)} {count === 1 ? 'person has' : 'people have'} no national ID recorded and
      cannot be compared across programmes — they are invisible to this figure.
    </p>
  )
}

// ── money ───────────────────────────────────────────────────────────────────

/**
 * A money total, or the refusal to add two currencies together.
 *
 * PollSphere shipped five money columns that disagreed about which currency
 * they were in and then divided them by each other.
 */
export function Money({ value, currency, mixedReason, className, precise }: {
  value?: string | number | null
  currency?: string | null
  mixedReason?: string | null
  className?: string
  /**
   * Keeps the minor units. Rounding is right for a portfolio total and wrong
   * for a RECONCILIATION DIFFERENCE: a gap of KES 0.40 printed as "difference
   * KES 0" inside a red banner headed "Rollup and ledger disagree", which reads
   * as the page contradicting itself.
   */
  precise?: boolean
}) {
  if (mixedReason) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground" title={mixedReason}>
        <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" aria-hidden />
        <span className="text-erp-xs">mixed currencies</span>
      </span>
    )
  }
  if (value === null || value === undefined) return <Unavailable />
  const n = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(n)) return <Unavailable />
  return (
    <span className={cn('tabular-nums', className)}>
      {currency ? `${currency} ` : ''}
      {precise
        ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : fmtNumber(Math.round(n))}
    </span>
  )
}

/** The share of spend with no beneficiary attached — a village water tank. */
export function UnattributedNote({ amount, currency, count, mixedReason }: {
  amount?: string | number | null
  currency?: string | null
  count?: number | null
  /**
   * Set on a mixed selection. The note renders nothing rather than a total —
   * relying on the server to have nulled `spendUnattributed` is an assumption
   * about a field that is independently nullable, and this is a money figure.
   */
  mixedReason?: string | null
}) {
  if (mixedReason) return null
  const n = amount === null || amount === undefined ? 0 : Number(amount)
  if (!n) return null
  return (
    <p className="text-erp-xs text-muted-foreground">
      Cost per person excludes <Money value={n} currency={currency} /> of spend
      {count ? ` across ${fmtNumber(count)} entries` : ''} with no beneficiary attached.
    </p>
  )
}

// ── truncation & provenance ─────────────────────────────────────────────────

/**
 * A capped result says so IN THE PAGE, not in a tooltip.
 *
 * PollSphere silently capped its overlap join at 50,000 rows with no ordering
 * and reported the totals as though they were complete.
 */
export function Truncation({ truncatedTo, totalAvailable, orderedBy, noun = 'rows' }: {
  truncatedTo: number; totalAvailable: number; orderedBy?: string | null; noun?: string
}) {
  if (totalAvailable <= truncatedTo) return null
  return (
    <p className="flex items-start gap-1.5 text-erp-xs text-muted-foreground">
      <Scissors className="h-3 w-3 mt-0.5 shrink-0" aria-hidden />
      <span>
        Showing {fmtNumber(truncatedTo)} of {fmtNumber(totalAvailable)} {noun}
        {orderedBy ? <>, ranked by <span className="font-medium">{orderedBy}</span></> : null}.
      </span>
    </p>
  )
}

/**
 * The envelope every analytics response carries: when it was computed, and
 * WHICH QUESTION it answers.
 *
 * The scoping family matters more than it looks. A ward coordinator sees a
 * national programme's whole totals under PROGRAMME scope and only their own
 * ward's rows under DELIVERY. Both are right, and a reader who does not know
 * which one they are looking at will put them in the same sentence.
 */
export function EnvelopeBar({ env, right }: { env?: Partial<AnalyticsEnvelope>; right?: ReactNode }) {
  if (!env) return null
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-erp-xs text-muted-foreground">
      <ScopingBadge code={env.scopingFamily} />
      {env.asOfDate && <span>as of {fmtDate(env.asOfDate)}</span>}
      {env.computedAt && <span>computed {fmtDateTime(env.computedAt)}</span>}
      {right}
    </div>
  )
}

/**
 * A rollup figure and a live figure in the same response have different
 * vintages, and a dashboard that stamps one timestamp on both is lying about
 * one of them.
 */
export function LiveNote({ children }: { children: ReactNode }) {
  return <p className="text-erp-xs text-muted-foreground italic">{children}</p>
}

// ── gates ───────────────────────────────────────────────────────────────────

/**
 * A statistic below its sample gate is not shown — it is explained.
 *
 * PollSphere ran Benford's law over zero disbursements, got the MAD of an empty
 * set (0.111), classified that as `suspect` and rendered "investigate potential
 * fabrication or padding". Every programme that had not yet paid anybody was
 * accused of fraud by its own dashboard.
 */
export function NotRun({ reason, what }: { reason?: string | null; what: string }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center">
      <p className="text-erp-sm font-medium text-foreground">{what} was not run</p>
      <p className="mt-1 text-erp-xs text-muted-foreground">
        {reason ?? 'There is not enough data for this analysis to say anything.'}
      </p>
    </div>
  )
}

/** A planning input, never a result. Always labelled. */
export function PlannedChip({ value }: { value?: string | number | null }) {
  if (value === null || value === undefined) return null
  const n = Number(value)
  if (Number.isNaN(n)) return null
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-muted/40 px-2 py-0.5 text-erp-xs text-muted-foreground"
      title="A planning input somebody typed when the programme was designed. It is never combined with a measured figure."
    >
      Planned {n > 0 ? '+' : ''}{n} pp
    </span>
  )
}

/**
 * A meter for "value against target".
 *
 * ## `direction` is not optional decoration
 *
 * The fill is coloured by ATTAINMENT, and attainment is a different quantity for
 * each direction. A drop rate of 90 against a ceiling of 100 is 90% of the way
 * to the ceiling — colouring by that paints the worst possible number emerald.
 * Under `TARGET_IS_BEST`, 150% of target is as wrong as 50% and used to clamp to
 * a full green bar.
 *
 * So the bar's LENGTH is value against target, clamped because a bar cannot be
 * longer than its track; the NUMBER beside it is unclamped, because "150%" is
 * the fact and clamping it to 100% next to an amber bar is the page arguing with
 * itself; and the TONE comes from attainment.
 *
 *   HIGHER_IS_BETTER  attainment = ratio            (1.0 → 100, 0.4 → 40)
 *   LOWER_IS_BETTER   attainment = 1 − ratio        (0.0 → 100, 0.9 → 10, ≥1 → 0)
 *   TARGET_IS_BEST    attainment = 1 − 2|1 − ratio|  (symmetric either side)
 */
export function Meter({ value, max, reason, direction = 'HIGHER_IS_BETTER' }: {
  value?: number | null
  max?: number | null
  reason?: string | null
  direction?: 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER' | 'TARGET_IS_BEST' | 'UNKNOWN'
}) {
  if (max === null || max === undefined || value === null || value === undefined) {
    return <Unavailable reason={reason ?? 'No denominator.'} short />
  }
  // A target of ZERO is a real target — "no defects", "no unreconciled entries" —
  // and `!max` used to send it down the "no denominator" path. There is no ratio
  // to compute against it, so attainment is the binary the target actually
  // states.
  if (max === 0) {
    if (direction !== 'LOWER_IS_BETTER' && direction !== 'TARGET_IS_BEST') {
      return <Unavailable reason={reason ?? 'No denominator.'} short />
    }
    const met = value === 0
    return (
      <div className="flex items-center gap-2">
        <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
          <div className={cn('h-full rounded-full', met ? 'bg-emerald-500' : 'bg-red-500')}
            style={{ width: met ? '100%' : '100%' }} />
        </div>
        <span className="tabular-nums text-erp-xs">{met ? 'met' : 'missed'}</span>
      </div>
    )
  }

  const ratio = value / max
  const width = Math.max(0, Math.min(100, ratio * 100))
  const attainment = direction === 'LOWER_IS_BETTER'
    ? Math.max(0, 1 - ratio) * 100
    : direction === 'TARGET_IS_BEST'
      // Doubled so that being a third off target is amber rather than green:
      // ±15% is still "on target", ±50% is not.
      ? Math.max(0, 1 - Math.abs(1 - ratio) * 2) * 100
      : Math.max(0, Math.min(1, ratio)) * 100
  const tone = attainment >= 70 ? 'bg-emerald-500' : attainment >= 40 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${width}%` }} />
      </div>
      <span className="tabular-nums text-erp-xs">{(ratio * 100).toFixed(0)}%</span>
    </div>
  )
}

/**
 * What to do about a selection that spans two currencies.
 *
 * The server's own sentence ends "Filter to one to see a total", and there is
 * no currency filter to reach for: `AnalyticsFilter` carries statuses,
 * categories, a unit, target groups, an owner and a date range, and nothing
 * else. So the instruction as written sends the reader looking for a control
 * that does not exist, and the second time that happens they stop believing the
 * page.
 *
 * Until the backend takes a currency, the honest guidance is the one that
 * works: narrow by something it DOES take until the selection holds one
 * currency, and the programmes list is where each programme's currency is
 * visible. The server's sentence is still shown verbatim — it is the one that
 * names which currencies were found.
 */
export function MixedCurrencyNote({ reason, what }: { reason?: string | null; what: string }) {
  const [rows, setRows] = useState<{ currency: string; names: string[] }[] | null>(null)
  const [loading, setLoading] = useState(false)

  // "Narrow it until it holds one currency" is only actionable if the reader can
  // find out WHICH programmes are the odd ones out, and no analytics response
  // says. The programme list does — `currency` is on every row — so the answer
  // is one fetch away, and it is fetched on demand rather than on four screens
  // that may never need it.
  const explainWhich = async () => {
    setLoading(true)
    try {
      const page = await listPrograms({}, undefined, 200)
      const by = new Map<string, string[]>()
      page.content.forEach((p) => {
        if (!p.currency) return
        by.set(p.currency, [...(by.get(p.currency) ?? []), p.name])
      })
      setRows([...by.entries()]
        .map(([currency, names]) => ({ currency, names }))
        .sort((a, b) => a.names.length - b.names.length))
    } catch { setRows([]) } finally { setLoading(false) }
  }

  if (!reason) return null
  return (
    <div
      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-erp-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
      role="status"
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p>{reason}</p>
        <p className="mt-1 text-erp-xs">
          {what} is withheld rather than added up across currencies. Narrow the selection by
          category, status or unit until it holds one currency —{' '}
          <Link to="/programs" className="underline underline-offset-2">the programmes list</Link>
          {' '}shows which currency each programme uses.
        </p>
        {rows === null ? (
          <button type="button" className="mt-1.5 text-erp-xs underline underline-offset-2"
            onClick={() => void explainWhich()} disabled={loading}>
            {loading ? 'Checking…' : 'Which programmes are in which currency?'}
          </button>
        ) : rows.length === 0 ? (
          <p className="mt-1.5 text-erp-xs">The programme list could not be read just now.</p>
        ) : (
          <ul className="mt-1.5 space-y-0.5 text-erp-xs">
            {rows.map((r) => (
              <li key={r.currency}>
                <span className="font-medium">{r.currency}</span> — {r.names.length}:{' '}
                <span className="opacity-90">
                  {r.names.slice(0, 6).join(', ')}
                  {r.names.length > 6 ? `, and ${r.names.length - 6} more` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
